import {
    type ActorEvent,
    EVENT_TYPE,
    type FieldADTValue,
    type HighLightConditions,
    type RecordLikeProtocol,
    type RichTextContentProtocol,
    type ViewField
} from '@lighthouse/core'
import { conversionHighLightRecord, selectRecordFromPool, useActionRunningLoadings, useAtomData } from '@lighthouse/shared'
import type { VirtualItem } from '@tanstack/react-virtual'
import cls from 'classnames'
import React, { memo, useCallback, useDeferredValue, useMemo } from 'react'
import styled from 'styled-components'

import { RecordOperator } from '../../components/RecordOperator'
import { useViewBlockContext } from '../../context'
import { AdvancedTableChecker, SCxAdvancedTableCheckerWrapper } from './AdvancedTableChecker'
import type { AdvancedTableContentState, AdvancedTableTooltipState } from './AdvancedTableContent'
import { TableAdvancedValue } from './AdvancedTableValue'

interface AdvancedTableRecordProps {
    index: number
    id: string
    checkable?: boolean
    selectedRecords?: string[]
    viewFields: ViewField[]
    highLightRules?: HighLightConditions
    columnVirtualItems: VirtualItem[]
    hoverCellColor: string
    primaryDataSourceFieldIds?: Set<string>
    onRecordSelect?: (recordIds: string[]) => void
    onOpenEditor?: (data: AdvancedTableContentState) => void
    onOpenTooltip?: (data: AdvancedTableTooltipState) => void
    onCellChange?: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
}

const SCxTableDot = styled.div`
    /* position: sticky;
    top: 0;
    right: 8px;
    z-index: 1;

    background: var(--color-white);
    overflow-y: visible; */
    position: sticky;
    right: 0;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--color-white);
    width: 36px;
    height: 100%;
`

const SCxTableOperator = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`

const SCxTableRecord = styled.div`
    position: relative;
    display: flex;
    width: max-content;
    min-width: 100%;
    height: 41px;
    border-bottom: 1px solid var(--color-gray-200);
    background: var(--color-white);

    &.record-active {
        background-color: var(--color-gray-100);

        ${SCxAdvancedTableCheckerWrapper} {
            background-color: var(--color-gray-100);
        }

        ${SCxTableOperator} {
            background-color: var(--color-gray-100);
        }
    }

    &:last-child {
        height: 42px;
    }

    &:last-child ${SCxAdvancedTableCheckerWrapper} {
        height: 42px;
    }

    &:hover {
        background-color: var(--color-gray-100);
    }

    &:hover .table-check {
        background-color: var(--color-gray-100);
    }

    &:hover ${SCxTableOperator} {
        background-color: var(--color-gray-100);
    }
`

const SCxTableRow = styled.div`
    width: 100%;
    display: flex;
`

const TableRecord: React.FC<AdvancedTableRecordProps> = memo(
    ({
        index,
        id,
        checkable,
        selectedRecords,
        viewFields,
        highLightRules,
        columnVirtualItems,
        hoverCellColor,
        primaryDataSourceFieldIds,
        onRecordSelect,
        onOpenEditor,
        onOpenTooltip,
        onCellChange,
        onRecordOperatorActionTrigger,
        onRecordClick,
        onRenderButtonTitle
    }) => {
        const { recordPoolAtom, dataSource, blockData, uniqueId } = useViewBlockContext()
        const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
        const { id: dsId, schema } = dataSource
        const { id: blockId, config } = blockData
        const { eventList, recordOperator } = config
        const { cell: cellStyleConditions, row: rowStyleConditions } = useMemo(
            () => conversionHighLightRecord(highLightRules ?? []),
            [highLightRules]
        )

        // 使用 Map 的 O(1) 查找，useMemo 保持 selector 引用稳定
        const selector = useMemo(() => selectRecordFromPool(dsId, id), [dsId, id])
        const record = useDeferredValue(useAtomData(recordPoolAtom, selector))

        const handleCheck = useCallback(
            (checked: boolean, id: string) => {
                if (!selectedRecords) {
                    return
                }
                if (checked) {
                    onRecordSelect?.([...selectedRecords, id])
                } else {
                    onRecordSelect?.(selectedRecords.filter(rId => rId !== id))
                }
            },
            [onRecordSelect, selectedRecords]
        )

        const handleRecordOpen = useCallback(() => {
            if (!onRecordClick || !record) {
                return
            }

            const recordClickEvent = eventList?.find(ev => ev.type === EVENT_TYPE.RECORD_CLICK)
            if (!recordClickEvent) {
                return
            }
            handleActionTriggerWithLoading({
                id: uniqueId,
                actorEvent: recordClickEvent,
                trigger: (id, actorEvent) => onRecordClick(id, actorEvent, record)
            })
        }, [eventList, handleActionTriggerWithLoading, onRecordClick, record, uniqueId])

        const handleActionTrigger = useCallback(
            async (id: string, actorEvent: ActorEvent) => {
                await onRecordOperatorActionTrigger?.(id, actorEvent, record)
            },
            [onRecordOperatorActionTrigger, record]
        )

        const handleRenderButtonTitle = useCallback(
            (v: RichTextContentProtocol) => {
                return onRenderButtonTitle(v, record)
            },
            [onRenderButtonTitle, record]
        )

        if (!record) {
            return null
        }

        return (
            <SCxTableRecord
                key={id}
                data-key={`${uniqueId}&${id}`}
                data-record-id={id}
                className={cls({ 'record-active': selectedRecords?.includes(id) })}
            >
                {selectedRecords && (
                    <AdvancedTableChecker
                        className="table-check"
                        checkable={checkable}
                        isRecordSelecting={selectedRecords.length > 0}
                        index={index}
                        showOpenerIcon={!!onRecordClick}
                        checked={selectedRecords.includes(id)}
                        onChange={checked => handleCheck(checked, id)}
                        onRecordOpen={handleRecordOpen}
                    />
                )}
                <SCxTableRow>
                    {columnVirtualItems.map(virtualColumn => {
                        const viewField = viewFields[virtualColumn.index]
                        const { fieldId: cId, width } = viewField
                        const field = schema[cId]
                        if (!record || !field) {
                            return null
                        }
                        const cData = record.content?.[cId]?.value
                        const styleConditions = cellStyleConditions?.[cId] || []
                        return (
                            <TableAdvancedValue
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: `${virtualColumn.size}px`,
                                    transform: `translateX(${virtualColumn.start}px)`
                                }}
                                blockId={uniqueId}
                                key={cId}
                                width={width}
                                recordId={id}
                                field={field}
                                data={cData}
                                styleConditions={styleConditions}
                                hoverCellColor={hoverCellColor}
                                primaryDataSourceFieldIds={primaryDataSourceFieldIds}
                                onOpenEditor={onOpenEditor}
                                onOpenTooltip={onOpenTooltip}
                                onCellChange={onCellChange}
                            />
                        )
                    })}
                </SCxTableRow>
                <SCxTableDot>
                    <SCxTableOperator>
                        <RecordOperator
                            uniqueId={uniqueId}
                            actions={recordOperator}
                            recordId={id}
                            record={record}
                            onActionTrigger={handleActionTrigger}
                            onRenderButtonTitle={handleRenderButtonTitle}
                        />
                    </SCxTableOperator>
                </SCxTableDot>
            </SCxTableRecord>
        )
    }
)

export default TableRecord
